import React, { useState, useEffect } from "react";
import {
  PaginationState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
  getFilteredRowModel,
} from "@tanstack/react-table";
import "./customTable.scss";
import NofoundLoader from "../../../widgets/nofound";
import ReactPaginate from "react-paginate";

interface tableProps {
  columns: any[];
  data: any[];
  showPagination: boolean;
  isSearchable: boolean;
  pageSize?: number;
  onSelectRow?: (rowData: any) => void;
  showRadio?: boolean;
  getSuggestionTitleColor?: any;
  groupTitle?: string;
}

const CustomTable: React.FC<tableProps> = ({
  columns,
  data,
  showPagination,
  pageSize = 10,
  getSuggestionTitleColor,
  groupTitle,
}) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize,
  });
  const [gFilter, setGFilter] = useState();
  const [isAccordionOpen, setIsAccordionOpen] = useState(true);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: { pagination, globalFilter: gFilter },
    onGlobalFilterChange: setGFilter,
    getRowCanExpand: () => true,
    autoResetAll: false,
  });

  const pageCount = table.getPageCount();
  const pageIndex = table.getState().pagination.pageIndex + 1;
  const headerGroups = table.getHeaderGroups();

  const handlePageClick = (data: any) => {
    const selectedPage = data.selected;
    setPagination({
      ...pagination,
      pageIndex: selectedPage,
    });
  };

  useEffect(() => {
    setPagination((prevPagination) => {
      const maxPageIndex = Math.max(0, Math.ceil(data.length / prevPagination.pageSize) - 1);
      return {
        ...prevPagination,
        pageIndex: Math.min(prevPagination.pageIndex, maxPageIndex),
      };
    });
  }, [data]);

  const headerRowMinWidth = (columnKey: any) => {
    switch (columnKey) {
      case "serial":
        return "55px";
      case "stage":
        return "300px";
      case "articletitle":
        return "150px";
      case "status":
        return "205px";
      case "action":
        return "200px";
      case "currentlywith":
        return "120px";
      case "journal":
        return "50px";
      case "authorname":
        return "250px";
      case "role":
        return "130px";
      case "assignjournal":
        return "200px";
      case "email":
        return "255px";
      case "firstname":
        return "100px";
      case "lastname":
        return "100px";
      default:
        return "150px";
    }
  };

  const hasRevisecount = data.some(
    (row) => row.revisecount !== undefined && row.revisecount !== null
  );
  const isAccordionEnabled = groupTitle && (groupTitle === "First review" || groupTitle.startsWith("Revision"));

  return (
    <>
      {isAccordionEnabled && (
        <p
          className="revision-class"
          onClick={() => setIsAccordionOpen(!isAccordionOpen)}
        >
          {groupTitle}
          <span className="material-icons" style={{ fontSize: "15px" }}>
            {isAccordionOpen ? "expand_less" : "expand_more"}
          </span>
        </p>
      )}
      {isAccordionOpen && (
        <div className="table-container">
          <table className="w-100">
            <thead className="thead">
              {headerGroups.map((headerGroup) => (
                <tr key={headerGroup.id} className="tr">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header?.id}
                      colSpan={header.colSpan}
                      className="th"
                      style={{ minWidth: headerRowMinWidth(header.id) }}
                    >
                      <div
                        className={
                          header.column.getCanSort()
                            ? "cursor-pointer select-none"
                            : ""
                        }
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: (
                            <span style={{ marginLeft: "15px" }}>
                              <span
                                className="material-icons"
                                style={{ fontSize: "11px" }}
                              >
                                expand_less
                              </span>
                            </span>
                          ),
                          desc: (
                            <span style={{ marginLeft: "15px" }}>
                              <span
                                className="material-icons"
                                style={{ fontSize: "11px" }}
                              >
                                expand_more
                              </span>
                            </span>
                          ),
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td className="no-data-found td" colSpan={columns.length}>
                    <NofoundLoader />
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => {
                  return (
                    <>
                      <tr key={`row_${row.id}`}>
                        {row.getVisibleCells().map((cell, index) => {
                          const isStatusColumn = cell?.column?.id === "status";
                          const minWidth = isStatusColumn ? "115px" : "auto";
                          const maxWidth = isStatusColumn ? "250px" : "auto";

                          return (
                            <td
                              className="td"
                              key={`cell_${cell.id}_${index}`}
                              style={{ minWidth, maxWidth }}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          );
                        })}
                      </tr>
                      {row.getIsExpanded() && (
                        <tr key={`row_${row.id}_exp`}>
                          <td
                            className="td"
                            colSpan={columns?.length}
                            style={{ textAlign: "start" }}
                          >
                            <p className="pt-15">
                              <span className="bold">Addtional Comments</span>
                              <br />
                              <p className="pt-15 d-flex gap-10">
                                Status
                                <span
                                  className="cellStyle"
                                  style={{
                                    color: getSuggestionTitleColor(
                                      row?.original?.suggestion_name
                                    ),
                                    backgroundColor: "rgb(244, 245, 250)",
                                    marginTop: "-5px",
                                  }}
                                >
                                  {row?.original?.suggestion_name}
                                </span>
                              </p>
                              <div
                                style={{
                                  backgroundColor: "#f4f5fa",
                                  marginRight: "20px",
                                  borderRadius: "10px",
                                }}
                              >
                                <p style={{ padding: "20px" }}>
                                  {row?.original?.suggestion_msg}
                                </p>
                              </div>
                            </p>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
      {(showPagination && data.length > 0) && (
        <div
          className="mt-4 pagination"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <div
            style={{
              marginRight: "15px",
              marginLeft: "8px",
              fontSize: "14px",
              padding: 0,
            }}
          >
            <span className="f-14">Showing </span>
            <span>
              {pageIndex} of {pageCount}
            </span>
            {" - "}
            <span>{data.length}</span>
            <span className="f-14"> entries</span>
          </div>
          <ReactPaginate
            previousLabel={
              <span className="material-icons action-arrow-icon">
                chevron_left
              </span>
            }
            nextLabel={
              <span className="material-icons action-arrow-icon">
                chevron_right
              </span>
            }
            breakLabel={"..."}
            breakClassName={"break-me"}
            pageCount={pageCount}
            marginPagesDisplayed={1}
            pageRangeDisplayed={5}
            onPageChange={handlePageClick}
            containerClassName={"pagination"}
            activeClassName={"active"}
          />
        </div>
      )}
    </>
  );
};

export default CustomTable;
